<template>

  <div>

    <!-- TODO: set :submit-button="false" if this location has no iq_configs -->
    <Modal
      title="Dynamic Rates"
      :submit="submit"
      :submitting="submitting"
    >

      <!-- TODO: show this if this location has no iq_configs -->
      <v-alert
        v-if="false"
        color="info"
        text="No SpotHero IQ policies are defined for this location."
      />

      <!-- TODO: hide this if this location has no iq_configs -->
      <v-form v-if="true">

        <v-table>
          <thead>
            <tr>
              <th scope="col">Policy</th>
              <th scope="col">Type</th>
              <th scope="col" style="min-width: 160px">Threshold</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="[fieldName, fieldValue] of Object.entries(formState)"
            >
              <td>
                <strong>{{ getPolicyTitle(fieldName) }}</strong><br>
                {{ getPolicyDescription(fieldName) }}
              </td>
              <td>{{ getEnumLabel(getPolicyType(fieldName), SpotHeroIQPolicyTypes) }}</td>
              <td>
                <o-text-field
                  type="number"
                  prefix="$"
                  v-model.number="v$[fieldName].$model"
                  :validator="v$[fieldName]"
                  clearable
                />
              </td>
            </tr>
          </tbody>
        </v-table>
        
      </v-form>

    </Modal>
  </div>

</template>

<script setup>

import { onBeforeMount, reactive, ref } from 'vue'

// models
import ChannelDefaultRate from '../../../../../models/ChannelDefaultRate'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { minValue, maxValue } from '../../../../../utils/i18n-validators'
import dollarAmount from '../../../../../rules/dollarAmount'

// misc
import NotificationService from '../../../../../services/NotificationService'
import RatesApi from '../../../../../services/api-clients/RatesApi'
import { closeDialog } from 'vue3-promise-dialog'
import { SpotHeroIQPolicyTypes } from '../../../../../enums'
import getEnumLabel from '../../../../../utils/getEnumLabel'

const props = defineProps({

  locationID: {
    type: Number,
    required: true,
  },

  channelDefaultRate: {
    type: ChannelDefaultRate,
    required: true,
  },

})

/**
 * Form state
 */

const formState = reactive({
  weekday_floor_rate: null,
  weekend_floor_rate: null,
  overnight_floor_rate: null,
  commuter_floor_rate: null,
  "24_hour_floor_rate": null,
  commuter_ceiling_rate: null,
  "24_hour_ceiling_rate": null,
})

// TODO: implement any other rules that are enforced by SpotHero

const formRules = reactive({
  weekday_floor_rate: {

  },
  weekend_floor_rate: {

  },
  overnight_floor_rate: {
    
  },
  commuter_floor_rate: {
    notGreaterThanCeiling: helpers.withMessage(
      'Can\'t be greater than Commuter Ceiling Rate', (value) => {
        return !helpers.req(value) || !helpers.req(formState["commuter_ceiling_rate"]) || value <= formState["commuter_ceiling_rate"]
      },
    ),
  },
  "24_hour_floor_rate": {
    notGreaterThanCeiling: helpers.withMessage(
      'Can\'t be greater than 24-Hour Ceiling Rate', (value) => {
        return !helpers.req(value) || !helpers.req(formState["24_hour_ceiling_rate"]) || value <= formState["24_hour_ceiling_rate"]
      },
    ),
  },
  commuter_ceiling_rate: {
    notLessThanFloor: helpers.withMessage(
      'Can\'t be less than Commuter Floor Rate', (value) => {
        return !helpers.req(value) || !helpers.req(formState["commuter_floor_rate"]) || value >= formState["commuter_floor_rate"]
      },
    ),
  },
  "24_hour_ceiling_rate": {
    notLessThanFloor: helpers.withMessage(
      'Can\'t be less than 24-Hour Floor Rate', (value) => {
        return !helpers.req(value) || !helpers.req(formState["24_hour_floor_rate"]) || value >= formState["24_hour_floor_rate"]
      },
    ),
  },
})

const v$ = useVuelidate(formRules, formState)

const policyMetadata = {}

/**
 * Initialization
 */

onBeforeMount(() => {

  if (
    !props.channelDefaultRate.channelRateData ||
    !props.channelDefaultRate.channelRateData.iq_config
  ) {
    NotificationService.error('SpotHero IQ config not found!')
    return
  }

  populateForm(props.channelDefaultRate.channelRateData.iq_config)

})

function populateForm(policies) {

  const keepPolicyNames = []

  policies
    // Sort so that "Floor" policies are listed first
    .sort((policy1, policy2) => {
      if (policy1.type === 'floor' || policy2.type === 'ceiling') {
        return -1
      } else if (policy1.type === 'ceiling' || policy2.type === 'floor') {
        return 1
      }
      return 0
    })
    .forEach((policy) => {

      const policyName = policy.policy
      keepPolicyNames.push(policyName)
      
      formState[policyName] = policy.threshold

      // If there's no object in formRules for this policy (i.e., it's a policy
      // that we don't know about), then add one.
      if (!(policyName in formRules)) {
        formRules.value.policyName = {}
      }

      formRules[policyName].nonNegative = helpers.withMessage('Must be at least $0.01', minValue(0.01))
      formRules[policyName].dollarAmount = helpers.withMessage('Must be a valid dollar amount', dollarAmount)

      switch (policy.type) {
        case 'floor':
          formRules[policyName].lessThan250 = helpers.withMessage('Must be less than $250.00', maxValue(249.99))
          break
        case 'ceiling':
          formRules[policyName].moreThan2 = helpers.withMessage('Must be more than $3.00', minValue(3.01))
          break
      }

      policyMetadata[policyName] = {
        title: policy.title,
        description: policy.description,
        type: policy.type,
      }

    })

  // Remove any policies from formState and formRules that weren't found in
  // the channelRateData

  Object.keys(formState).forEach((policyName) => {
    if (!keepPolicyNames.includes(policyName)) {
      delete formState[policyName]
    }
  })

  Object.keys(formRules).forEach((policyName) => {
    if (!keepPolicyNames.includes(policyName)) {
      delete formRules[policyName]
    }
  })

}

/**
 * Submission
 */

const submitting = ref(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitStatus.setBusy()

  const payload = Object.keys(formState).map((policyName) => {
    return {
      policy: policyName,
      threshold: formState[policyName] === '' ? null : formState[policyName],
    }
  })

  RatesApi.updateSpotHeroIQRates(props.locationID, payload)
    .then((updatedChannelRateData) => {
      props.channelDefaultRate.channelRateData = updatedChannelRateData
      props.channelDefaultRate.$save()
      NotificationService.success('SpotHero IQ configuration updated')
      closeDialog(null)
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(error)
    })
    .finally(() => {
      submitting.value = true
    })

}

/**
 * Miscellaneous
 */

function getPolicyTitle(policyName) {
  return policyName in policyMetadata ? policyMetadata[policyName].title : policyName
}

function getPolicyDescription(policyName) {
  return policyName in policyMetadata ? policyMetadata[policyName].description : ''
}

function getPolicyType(policyName) {
  return policyName in policyMetadata ? policyMetadata[policyName].type : ''
}

</script>
